import { useEffect, useRef, useState } from "react";
import {
  getProfiles, setActiveProfile, updateProfile, setProfileName,
  computeConsensus, computeDivergence, replaceProfiles,
} from "../lib/profiles.js";
import { api } from "../api.js";

// Drie modes: matthijs / partner / consensus
//
// In consensus-mode worden de sliders DISABLED en geeft de Sidebar
// de gemiddelde gewichten door. Divergentie wordt getoond als hint.
export function ProfileSwitcher({ weights, enforceVeto, retreatMode,
                                  onApplyProfile }) {
  const [state, setState] = useState(getProfiles());
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const hydrated = useRef(false);

  // Centrale opslag op de server, zodat beide partners elkaars
  // profiel-aanpassingen zien (niet alleen lokaal per apparaat).
  const pushRemote = (next) => { api.putSetting("profiles", next).catch(() => {}); };

  // Bij opstarten: haal de gedeelde profielen van de server en pas het
  // actieve profiel toe op de app.
  useEffect(() => {
    api.getSetting("profiles").then(remote => {
      if (remote && remote.profiles) {
        const merged = replaceProfiles(remote);
        setState(merged);
        const target = merged.active === "consensus"
          ? computeConsensus(merged)
          : merged.profiles[merged.active];
        if (target) onApplyProfile(target);
      }
      hydrated.current = true;
    }).catch(() => { hydrated.current = true; });
  }, []);

  // Sync huidige App.jsx-state naar het actieve profiel (auto-save)
  useEffect(() => {
    if (state.active === "consensus") return;
    const cur = state.profiles[state.active];
    if (!cur) return;
    if (JSON.stringify(cur.weights) === JSON.stringify(weights)
        && cur.enforceVeto === enforceVeto
        && cur.retreatMode === retreatMode) return;
    const next = updateProfile(state.active, { weights, enforceVeto, retreatMode });
    setState(next);
    if (hydrated.current) pushRemote(next);
  }, [weights, enforceVeto, retreatMode]);

  const switchTo = (id) => {
    let target;
    if (id === "consensus") {
      target = computeConsensus(state);
    } else {
      target = state.profiles[id];
    }
    onApplyProfile(target);
    const next = setActiveProfile(id);
    setState(next);
    pushRemote(next);
  };

  const startEdit = (id) => {
    setEditingId(id);
    setEditName(state.profiles[id].name);
  };
  const commitEdit = () => {
    if (editingId && editName.trim()) {
      const next = setProfileName(editingId, editName.trim());
      setState(next);
      pushRemote(next);
    }
    setEditingId(null);
    setEditName("");
  };

  const div = computeDivergence(state);
  const divergentKeys = Object.entries(div).filter(([_, v]) => v >= 3);

  return (
    <div className="pf-switcher" role="tablist">
      <div className="pf-tabs">
        {["matthijs", "partner"].map(id => {
          const p = state.profiles[id];
          const active = state.active === id;
          if (editingId === id) {
            return (
              <input
                key={id}
                className="pf-tab pf-edit"
                value={editName}
                autoFocus
                onChange={e => setEditName(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={e => e.key === "Enter" && commitEdit()}
                maxLength={16}
              />
            );
          }
          return (
            <button
              key={id}
              role="tab"
              aria-selected={active}
              className={`pf-tab ${active ? "active" : ""}`}
              onClick={() => switchTo(id)}
              onDoubleClick={() => startEdit(id)}
              type="button"
              title="Dubbelklik om te hernoemen"
            >
              <span className="pf-emoji">{p.emoji}</span>
              <span className="pf-name">{p.name}</span>
            </button>
          );
        })}
        <button
          role="tab"
          aria-selected={state.active === "consensus"}
          className={`pf-tab pf-tab-consensus ${state.active === "consensus" ? "active" : ""}`}
          onClick={() => switchTo("consensus")}
          type="button"
        >
          <span className="pf-emoji">🤝</span>
          <span className="pf-name">Consensus</span>
        </button>
      </div>
      {state.active === "consensus" && divergentKeys.length > 0 && (
        <div className="pf-divergence-hint">
          <b>⚠ Hier zijn jullie het oneens:</b>
          <span>{divergentKeys.slice(0, 4).map(([k, v]) => `${k} (Δ${v})`).join(" · ")}</span>
        </div>
      )}
      {state.active === "consensus" && (
        <div className="pf-consensus-note">
          Sliders disabled — gewicht = gemiddelde van beide profielen. Schakel naar
          één persoon om aan te passen.
        </div>
      )}
    </div>
  );
}
